import type { MouseEvent, ReactNode } from 'react';

export enum DashboardDataTableColumnKind {
  Custom = 'custom',
  Text = 'text',
  Date = 'date',
  Status = 'status',
  Selection = 'selection',
  Actions = 'actions',
}

export enum DashboardTableStatusTone {
  Success = 'success',
  Warning = 'warning',
  Info = 'info',
  Danger = 'danger',
  Neutral = 'neutral',
}

export enum DashboardDataTableSortDirection {
  Asc = 'asc',
  Desc = 'desc',
}

export enum DashboardDataTableStickyEdge {
  Left = 'left',
  Right = 'right',
}

export interface DashboardDataTableColumn<TItem> {
  id: string;
  header: ReactNode;
  cell: (item: TItem) => ReactNode;
  className?: string | undefined;
  headerClassName?: string | undefined;
  sortable?: boolean | undefined;
  sortKey?: string | undefined;
  sticky?: DashboardDataTableStickyEdge | undefined;
  stickyOffset?: string | undefined;
}

interface DashboardDataTableBaseColumnDefinition {
  id: string;
  header: ReactNode;
  className?: string | undefined;
  headerClassName?: string | undefined;
  hidden?: boolean | undefined;
  sortable?: boolean | undefined;
  sortKey?: string | undefined;
  sticky?: DashboardDataTableStickyEdge | undefined;
  stickyOffset?: string | undefined;
}

export interface DashboardDataTableCustomColumnDefinition<
  TItem,
> extends DashboardDataTableBaseColumnDefinition {
  kind?: DashboardDataTableColumnKind.Custom | undefined;
  cell: (item: TItem) => ReactNode;
}

export interface DashboardDataTableTextColumnDefinition<
  TItem,
> extends DashboardDataTableBaseColumnDefinition {
  kind: DashboardDataTableColumnKind.Text;
  value: (item: TItem) => ReactNode;
  fallback?: ReactNode | undefined;
}

export interface DashboardDataTableDateColumnDefinition<
  TItem,
> extends DashboardDataTableBaseColumnDefinition {
  kind: DashboardDataTableColumnKind.Date;
  value: (item: TItem) => ReactNode;
}

export interface DashboardDataTableStatusColumnDefinition<
  TItem,
> extends DashboardDataTableBaseColumnDefinition {
  kind: DashboardDataTableColumnKind.Status;
  label: (item: TItem) => ReactNode;
  tone: (item: TItem) => DashboardTableStatusTone;
}

export interface DashboardDataTableSelectionColumnDefinition<TItem> extends Omit<
  DashboardDataTableBaseColumnDefinition,
  'header'
> {
  kind: DashboardDataTableColumnKind.Selection;
  checked: (item: TItem) => boolean;
  label: (item: TItem) => string;
  onChange: (item: TItem, checked: boolean) => void;
  header?: ReactNode | undefined;
  headerChecked?: boolean | 'indeterminate' | undefined;
  headerDisabled?: boolean | undefined;
  headerLabel?: string | undefined;
  onHeaderChange?: ((checked: boolean) => void) | undefined;
}

export interface DashboardDataTableActionsColumnDefinition<TItem> extends Omit<
  DashboardDataTableBaseColumnDefinition,
  'header'
> {
  kind: DashboardDataTableColumnKind.Actions;
  cell: (item: TItem) => ReactNode;
  header?: ReactNode | undefined;
}

export type DashboardDataTableColumnDefinition<TItem> =
  | DashboardDataTableCustomColumnDefinition<TItem>
  | DashboardDataTableTextColumnDefinition<TItem>
  | DashboardDataTableDateColumnDefinition<TItem>
  | DashboardDataTableStatusColumnDefinition<TItem>
  | DashboardDataTableSelectionColumnDefinition<TItem>
  | DashboardDataTableActionsColumnDefinition<TItem>;

export interface DashboardDataTablePaginationState {
  page: number;
  pageSize: number;
  totalItems: number;
}

export interface DashboardDataTablePaginationProps {
  value: DashboardDataTablePaginationState;
  pageSizes: readonly number[];
  disabled?: boolean | undefined;
  onChange: (value: DashboardDataTablePaginationState) => void;
}

export interface DashboardDataTableSortState {
  key: string;
  direction: DashboardDataTableSortDirection;
}

export interface DashboardDataTableSortProps {
  value?: DashboardDataTableSortState | undefined;
  onChange: (value: DashboardDataTableSortState | undefined) => void;
}

export interface DashboardDataTableFooterRow<TItem> {
  id: string;
  cell: (column: DashboardDataTableColumn<TItem>) => ReactNode;
  className?: string | undefined;
  cellClassName?: string | undefined;
}

export interface DashboardDataTableRowLink<TItem> {
  getHref: (item: TItem) => string;
  getLabel?: ((item: TItem) => string) | undefined;
  onNavigate?:
    ((href: string, item: TItem, event?: MouseEvent<HTMLTableRowElement>) => void) | undefined;
}

interface DashboardDataTableBaseProps<TItem> {
  tableLabel?: string | undefined;
  className?: string | undefined;
  surfaceClassName?: string | undefined;
  toolbar?: ReactNode | undefined;
  filterToolbar?: ReactNode | undefined;
  /**
   * Optional header inside the card, above the columns (e.g. dashboard widgets). When a card
   * title or card actions are set, the column header row gets the page tone.
   */
  cardTitle?: ReactNode | undefined;
  cardActions?: ReactNode | undefined;
  columns: DashboardDataTableColumn<TItem>[];
  items: TItem[];
  getRowKey: (item: TItem) => string;
  loading?: boolean | undefined;
  errorMessage?: ReactNode | undefined;
  emptyMessage?: ReactNode | undefined;
  loadingMessage?: ReactNode | undefined;
  pagination?: DashboardDataTablePaginationProps | undefined;
  sort?: DashboardDataTableSortProps | undefined;
  footerSummary?: ReactNode | undefined;
  footerRows?: DashboardDataTableFooterRow<TItem>[] | undefined;
  minWidthClassName?: string | undefined;
  fillHeight?: boolean | undefined;
  /**
   * While the table shows only its empty or error message, grow it to fill a column flex parent
   * and give the message the extra height. Rows and loading keep their natural height.
   */
  fillEmptyHeight?: boolean | undefined;
  maxBodyHeight?: number | undefined;
  rowLink?: DashboardDataTableRowLink<TItem> | undefined;
}

export type DashboardDataTableProps<TItem> = DashboardDataTableBaseProps<TItem> &
  (
    | {
        title: ReactNode;
        tableLabel?: string | undefined;
      }
    | {
        title?: undefined;
        tableLabel: string;
      }
  );

export interface DashboardDataTableStateOptions {
  initialPage?: number | undefined;
  initialPageSize: number;
  initialSort?: DashboardDataTableSortState | undefined;
  totalItems: number;
  pageSizes: readonly number[];
  disabled?: boolean | undefined;
}
